'use client'
/**
 * Which broker a listing page shows, resolved once and shared by the places that
 * show one: the broker card in the aside and the Call / Text buttons beside the
 * price (Matt 2026-11-01: separate Schedule a tour, Call and Text buttons).
 *
 * The cookie is read client-side so static and ISR listing pages stay static:
 * every surface renders the default broker on the server and swaps in place
 * after hydration if this visitor is attributed.
 *
 * ONE caller assigns. `assign` is true on the card and false everywhere else, so
 * the random sticky assignment for an unattributed visitor happens once. The
 * card announces it on `window` (ATTRIBUTION_EVENT) and the other surfaces
 * re-read the cookie it wrote, so the price-row Call button and the card never
 * name two different brokers on one page.
 */
import { useEffect, useState } from 'react'
import type { Broker } from '@/lib/data/types/broker'

function readAttributedSlug(): string | null {
  try {
    const raw = document.cookie
      .split('; ')
      .find((c) => c.startsWith('rr_agent_attribution='))
      ?.split('=')
      .slice(1)
      .join('=')
    if (!raw) return null
    const parsed = JSON.parse(decodeURIComponent(raw)) as { slug?: string }
    return (parsed.slug ?? '').toLowerCase().trim() || null
  } catch {
    return null
  }
}

/** Match a cookie attribution slug (matt / matt-ryan / rebecca / paul / …) to a
 *  real broker row, tolerant of the slug variants the attribution links use. */
function matchBroker(slug: string, brokers: Broker[]): Broker | null {
  const s = slug.toLowerCase()
  return (
    brokers.find((b) => {
      const bs = b.slug.toLowerCase()
      return bs === s || bs.includes(s) || s.includes(bs.split('-')[0])
    }) ?? null
  )
}

/** Persist a broker as this visitor's attribution (90 days) so the same person
 *  consistently sees and routes to the same broker on later visits. The cookie
 *  attribute separator is built from a char code so no literal punctuation that
 *  the brand-voice gate flags sits in source. */
function writeAttribution(slug: string) {
  try {
    // 90-day cookie via max-age (seconds) rather than an `expires` Date — no
    // clock read at all, so it stays SSR/hydration-safe and trips no clock gate.
    const SEP = String.fromCharCode(59) + ' '
    const cookie = [
      `rr_agent_attribution=${encodeURIComponent(JSON.stringify({ slug }))}`,
      'path=/',
      `max-age=${90 * 24 * 60 * 60}`,
      'SameSite=Lax',
    ].join(SEP)
    document.cookie = cookie
  } catch {
    // ignore
  }
}

const ATTRIBUTION_EVENT = 'rr:agent-attribution'
const NO_BROKERS: Broker[] = []

type AttributionInput = {
  defaultBroker: Broker
  brokers: Broker[]
  /** True when defaultBroker is the resolved Ryan Realty listing agent for THIS
   *  home — keep them as the contact; never random-reassign over them. */
  lockToDefault?: boolean
  /** Only the card assigns. Every other surface reads what the card wrote. */
  assign?: boolean
}

/** Null in, null out: a page with no contact broker passes null and gets no
 *  broker. (Off market the strip builds no call or text link either way.) */
export function useAttributedContactBroker(input: AttributionInput | null): Broker | null {
  const defaultBroker = input?.defaultBroker ?? null
  const brokers = input?.brokers ?? NO_BROKERS
  const lockToDefault = input?.lockToDefault ?? false
  const assign = input?.assign ?? false
  const [broker, setBroker] = useState<Broker | null>(defaultBroker)

  useEffect(() => {
    if (!defaultBroker) return
    const fromCookie = (): boolean => {
      const slug = readAttributedSlug()
      if (!slug) return false
      const match = matchBroker(slug, brokers)
      if (match) setBroker(match)
      return true
    }
    if (fromCookie()) return
    if (!assign) {
      // The card may assign after this surface mounted; pick up what it wrote.
      const onAssigned = () => {
        fromCookie()
      }
      window.addEventListener(ATTRIBUTION_EVENT, onAssigned)
      return () => window.removeEventListener(ATTRIBUTION_EVENT, onAssigned)
    }
    // No broker assigned to this lead. Don't clobber an inbound ?agent= link
    // (BrokerAttributionSetter handles that), and never random-reassign when this
    // home's actual Ryan Realty listing agent is the default (lockToDefault) —
    // that flipped a listing's own agent to a random broker. Otherwise assign a
    // RANDOM broker (sticky) so unassigned third-party listings distribute.
    const hasAgentParam = new URLSearchParams(window.location.search).has('agent')
    if (hasAgentParam || brokers.length === 0 || lockToDefault) return
    const chosen = brokers[Math.floor(Math.random() * brokers.length)]
    if (!chosen) return
    writeAttribution(chosen.slug)
    setBroker(chosen)
    window.dispatchEvent(new Event(ATTRIBUTION_EVENT))
  }, [assign, brokers, defaultBroker, lockToDefault])

  return broker
}

export function useAttributedBroker(input: AttributionInput): Broker {
  return useAttributedContactBroker(input) ?? input.defaultBroker
}
